import { useCallback, useEffect, useMemo, useRef, useState, Suspense } from 'react';
import {
  buildTrackingPath,
  getSemiPoints,
  isObject3D,
  isRef,
  /* useVirtualCourse, */
  MotionPathControls,
  useMotionPath,
  VirtualCourse,
  OverrideMaterial,
  Loader,
} from '@slope/r3f';
import { isCourse } from '@slope/three';
import { NavMeshHelper } from '@slope/navmesh/three';
import { CameraControls, PerspectiveCamera } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { isHole } from '@slope/three';
import { folder, useControls } from 'leva';
import { useCachedCameraPath, useFitToHoleBound } from '../../hooks';
import { useStore } from '@slope/store';
import { WindTrails } from './WindTrails';
//import { useWindTrailProps } from './useWindProps';
import { Panels } from './Panels';
import { focalOffsetXForScreenFrac } from '../../utils';

const Loop: React.FC<{
  factor?: number;
  focalXOffset?: number;
}> = ({ factor = 0.01, focalXOffset = 0.55 }) => {
  const motion = useMotionPath();
  const controls = useThree((state) => state.controls as CameraControls);
  useFrame(({ camera }, delta) => {
    motion.current += Math.min(1 / 60, delta) * factor;

    const position = motion.object.current.position;
    const target = motion.tracker.current.position;
    const distance = position.distanceTo(target);
    const xOffset = focalOffsetXForScreenFrac(focalXOffset, camera, distance);
    controls.setPosition(position.x, position.y, position.z, false);
    controls.setLookAt(position.x, position.y, position.z, target.x, target.y, target.z, false);
    controls.setFocalOffset(-xOffset, 0, 0, true);
  });

  return null;
};

const PathTracker: React.FC<{
  curve: THREE.Curve<THREE.Vector3>;
  offset?: number;
}> = ({ curve, offset = 0 }) => {
  const motion = useMotionPath();
  useFrame(() => {
    if (isRef(motion.tracker) && isObject3D(motion.tracker.current)) {
      curve
        .getPointAt((motion.offset + offset + 1) % 1, motion.tracker.current.position)
        .multiply(new THREE.Vector3(0.3, 0, 0.3));
    }
  });

  return null;
};

export type SceneProps = {
  tournamentId: string;
  slug?: string;
  selectedHole?: number | null;
};

export const Scene: React.FC<SceneProps> = ({ tournamentId, slug, selectedHole }) => {
  // Construct dynamic model URL based on club slug
  const modelUrl = useMemo(() => {
    if (!slug) return '';
    return `https://resources.nextcaddy.com/clubResources/${slug}/orbital/model.glb`;
  }, [slug]);

  const selectedHoleName = useMemo(
    () => (selectedHole ? `hole_${selectedHole}` : 'all'),
    [selectedHole],
  );

  const [scene, setScene] = useState(() => new THREE.Object3D());
  const panels = useStore(({ ui: { panels } }) => panels);
  const storeSet = useStore((state) => state.set);

  const trackerRef = useRef<THREE.Object3D>(null!);
  const navMesh = useMemo(() => {
    if (!isCourse(scene)) return null;
    const navMesh = new NavMeshHelper(scene.navMesh);
    navMesh.matrixWorld.copy(scene.matrixWorld);
    return navMesh;
  }, [scene]);

  const distance = useMemo(
    () =>
      navMesh
        ? new THREE.Box3().expandByObject(navMesh).getSize(new THREE.Vector3()).length()
        : 500,
    [navMesh],
  );

  const {
    motion,
    height,
    distance: cameraDistance,
    focalXOffset,
    speed,
  } = useControls(
    {
      camera: folder({
        motion: true,
        height: {
          value: distance,
          min: 0,
          max: 1000,
        },
        distance: {
          value: distance,
          min: 1,
          max: 1000,
        },
        speed: {
          value: 0.01,
          min: 0.001,
          max: 0.1,
          step: 0.001,
        },
        focalXOffset: {
          value: 0.55,
          min: 0,
          max: 1,
          step: 0.01,
        },
      }),
      showAutoFlyControls: false,
    },
    [distance, scene],
  );

  const { cameraPath, pending } = useCachedCameraPath({
    modelUrl,
    navMesh,
    height,
    distance: cameraDistance,
    // optional: keep paths distinct by selected hole etc.
    // extraKey: selectedHole,
    // segments: 240,
  });

  const fitHoleBound = useFitToHoleBound();

  /* useEffect(() => {
    if (selectedHoleName !== 'all' && isHole(scene)) {
      autoFlyOver(scene).then((paths) => {
        setCameraPathState(
          paths
            ? {
                trackerPoints: paths.tracker.getPoints(50),
                curvePoints: paths.curve.getPoints(50),
              }
            : null,
        );
      });
    }
  }, [autoFlyOver, scene, selectedHoleName]); */

  useEffect(() => {
    if (selectedHoleName !== 'all' && isHole(scene)) {
      fitHoleBound(scene)}
  }, [fitHoleBound, scene, selectedHoleName]);

  const semiPoints = useMemo(() => (scene && getSemiPoints(scene)) ?? [], [scene]);

  const trackingPath = useMemo(
    () => (cameraPath && semiPoints.length ? buildTrackingPath(cameraPath, semiPoints) : null),
    [cameraPath, semiPoints],
  );

  const radius = useMemo(() => {
    const box = new THREE.Box3().expandByObject(scene);
    const size = box.getSize(new THREE.Vector3());
    const max = Math.max(size.x, size.y, size.z);
    const min = Math.min(size.x, size.y, size.z);
    const scale = 1.5;
    return Math.max(max, min) * scale;
  }, [scene]);

  const ghostMaterial = useMemo(
    () =>
      new THREE.MeshBasicMaterial({
        color: '#c5c5c5',
        transparent: true,
        opacity: 0.2,
        //depthTest: false,
        //depthWrite: false,
        side: THREE.DoubleSide,
        blending: THREE.NormalBlending,
        polygonOffset: true,
        polygonOffsetFactor: 100,
        polygonOffsetUnits: -1,
      }),
    [],
  );

  /*const weatherData = useWeather();

  const windTrailProps = useWindTrailProps(scene, {
    pad: 1.2,
    lift: 10,
    densityPer100: 0.5,
    min: 5,
    max: 20,
    length: 10,
    speedMs: weatherData.windSpeedMs,
    dpr: Math.min(2, window.devicePixelRatio || 1),
  });*/

  const exlude = useCallback((child: THREE.Object3D) => {
    return /water/.test(child.name);
  }, []);

  const handleCourseLoad = useCallback(
    (scene: THREE.Object3D) => {
      setScene(scene);
      storeSet(({ course, origin }) => ({
        origin: isCourse(scene) ? { lat: scene.location.y, lng: scene.location.x } : origin,
        course: isCourse(scene) ? scene : course,
        hole: isHole(scene) ? scene : null,
      }));
    },
    [storeSet],
  );

  const center = useMemo(() => {
    const box = new THREE.Box3().expandByObject(scene);
    return box.getCenter(new THREE.Vector3());
  }, [scene]);

  const dummy = useRef(new THREE.Object3D());

  return (
    <>
      <ambientLight intensity={0.8 * Math.PI} />
      <spotLight
        penumbra={1}
        position={[radius, radius, radius]}
        intensity={1}
        castShadow={true}
        shadow-bias={-0.0001}
        shadow-normalBias={0}
        shadow-mapSize={1024}
      />
      <pointLight intensity={1 * Math.PI} position={[0, 6, 0]} decay={0} />
      <PerspectiveCamera makeDefault fov={60} position={[0, 1000, 0]} near={1} far={10000} />
      <primitive object={scene} dispose={null} />

      <Suspense fallback={<Loader message="Loading course..." />}>
        {modelUrl && (
          <>
            <VirtualCourse src={modelUrl} hole={selectedHoleName} onLoad={handleCourseLoad} />
            {selectedHoleName !== 'all' && (
              <OverrideMaterial material={ghostMaterial}>
                <VirtualCourse
                  src={modelUrl}
                  hole={'all'}
                  clone
                  receiveShadow={false}
                  castShadow={false}
                  exclude={exlude}
                />
              </OverrideMaterial>
            )}
          </>
        )}
      </Suspense>

      {pending && <Loader message="Calculating flight path..." />}

      {panels.enabled && <Panels tournamentId={tournamentId} />}
      {motion && cameraPath && (
        <MotionPathControls
          object={dummy}
          curve={cameraPath}
          tracker={trackerRef}
          motion={selectedHoleName === 'all' ? motion : false}
        >
          <Loop factor={speed} focalXOffset={focalXOffset} />
          {trackingPath && <PathTracker curve={trackingPath} />}
          {/* <MotionPathHelper /> */}
        </MotionPathControls>
      )}
      <CameraControls makeDefault smoothTime={1} />
      <object3D ref={trackerRef} position={[0, 0, 0]} />
      {selectedHoleName !== 'all' && (
        <WindTrails
          dirDegFrom={45}
          speedMs={10 * 2}
          color="#ffffff"
          width={30}
          length={10}
          decay={0.8}
          count={20}
          center={center}
          //{...windTrailProps}
        />
      )}

      {/*  <Coordinates latitude={37.349287350972496} longitude={-5.964347592989603}>
        <mesh>
          <sphereGeometry args={[5, 10, 10]} />
          <meshBasicMaterial
            transparent
            opacity={0.5}
            color="red"
            side={THREE.DoubleSide}
            depthTest={false}
          />
        </mesh>
      </Coordinates> */}
      {/* </View> */}
    </>
  );
};

// Note: Dynamic modelUrl is now used, so preload is handled internally by VirtualCourse
// useGLTF.preload(modelUrl);
